/**
 * What the renderer needs to draw a monster, read from the MONSTER stream's state. The C64 draws
 * a monster from its sprite frame alone (`ai.frame`, the walk cycle; `facing` follows it); these
 * names say which movement it is in.
 */

import { ANGRY_COLOUR, FROZEN_COLOUR, SPRITE_COLOUR } from './tuning';
import type { Monster } from '../types';

export type MonsterPose =
  /** Sliding in from the top at the level start. */
  | 'dropIn'
  /** Landed, waiting out its start delay (the walk cycle plays in place). */
  | 'wait'
  | 'walk'
  /** The 16-tick crouch before a vertical jump (it looks left and right). */
  | 'crouch'
  | 'jump'
  | 'fall'
  /** Out of a bubble, falling angry to the ground. */
  | 'escape'
  | 'fly'
  /** Hidegons, Drunk or Mighta standing still to throw (the 7-update wind-up), or Drunk awaiting its bottle. */
  | 'attack'
  /** Frozen by the power heart: light blue, not animating, falling to the floor and lying there; touch kills it. */
  | 'frozen'
  | 'trapped'
  | 'dead';

export function monsterPose(m: Monster): MonsterPose {
  const a = m.ai;
  switch (m.state) {
    case 'dropIn': return 'dropIn';
    case 'waiting': return 'wait';
    case 'escaping': return 'escape';
    case 'trapped': return 'trapped';
    case 'dead': return 'dead';
    default: break;
  }
  if (m.frozen) return 'frozen';
  if (m.kind === 'pulpul' || m.kind === 'monsta') return 'fly';
  if (a.attack >= 0 && m.kind !== 'invader') return 'attack';
  if (a.falling) return 'fall';
  if (a.jump >= 0x10) return 'crouch';
  if (a.jump >= 0) return 'jump';
  return 'walk';
}

/**
 * The C64 colour number of a monster's sprite: its kind's ($AB61 by state), light red when angry, light
 * blue when frozen by the power heart (D_2785 sets it every update, over the anger's).
 */
export function monsterColour(m: Monster): number {
  if (m.frozen) return FROZEN_COLOUR;
  return m.angry ? ANGRY_COLOUR : SPRITE_COLOUR[m.kind];
}
